<template>
  <ul
    class="pagination"
    :class="[size && `pagination-${size}`, align && `justify-content-${align}`]"
  >
    <!-- First page button - Spec 003 -->
    <li class="page-item first-page" :class="{ disabled: page === 1 }">
      <a class="page-link" aria-label="First" role="button" tabindex="0" @click="firstPage">
        <span aria-hidden="true"
          ><i class="fa fa-angle-double-left" aria-hidden="true"></i
        ></span>
      </a>
    </li>

    <!-- Previous page button -->
    <li class="page-item prev-page" :class="{ disabled: page === 1 }">
      <a class="page-link" aria-label="Previous" role="button" tabindex="0" @click="prevPage">
        <span aria-hidden="true"
          ><i class="fa fa-angle-left" aria-hidden="true"></i
        ></span>
      </a>
    </li>

    <!-- Page numbers -->
    <li
      class="page-item"
      :class="{ active: page === item }"
      :key="item"
      v-for="item in range(minPage, maxPage)"
    >
      <a class="page-link" role="button" tabindex="0" @click="changePage(item)">{{ item }}</a>
    </li>

    <!-- Next page button -->
    <li class="page-item next-page" :class="{ disabled: page === totalPages }">
      <a class="page-link" aria-label="Next" role="button" tabindex="0" @click="nextPage">
        <span aria-hidden="true"
          ><i class="fa fa-angle-right" aria-hidden="true"></i
        ></span>
      </a>
    </li>

    <!-- Last page button - Spec 003 -->
    <li class="page-item last-page" :class="{ disabled: page === totalPages }">
      <a class="page-link" aria-label="Last" role="button" tabindex="0" @click="lastPage">
        <span aria-hidden="true"
          ><i class="fa fa-angle-double-right" aria-hidden="true"></i
        ></span>
      </a>
    </li>
  </ul>
</template>
<script>
export default {
  name: "base-pagination",
  props: {
    modelValue: {
      type: Number,
      default: 1,
      description: "Current page (use with v-model)",
    },
    pageCount: {
      type: Number,
      default: 0,
      description:
        "Pagination page count. This should be specified in combination with perPage",
    },
    perPage: {
      type: Number,
      default: 10,
      description:
        "Pagination per page. Should be specified with total or pageCount",
    },
    total: {
      type: [String, Number],
      default: 0,
      description:
        "Can be specified instead of pageCount. The page count in this case will be total/perPage",
    },
    size: {
      type: String,
      default: "",
      description: "Pagination size",
    },
    align: {
      type: String,
      default: "",
      description: "Pagination alignment (e.g center|start|end)",
    },
  },
  emits: ["update:modelValue"],
  computed: {
    page() {
      const p = Number(this.modelValue) || 1;
      if (p < 1) return 1;
      if (p > this.totalPages) return this.totalPages;
      return p;
    },
    totalPages() {
      if (this.pageCount > 0) return this.pageCount;
      const total = Number(this.total) || 0;
      const perPage = Number(this.perPage);
      if (total > 0 && perPage > 0) {
        return Math.ceil(total / perPage);
      }
      return 1;
    },
    pagesToDisplay() {
      if (this.totalPages > 0 && this.totalPages < this.defaultPagesToDisplay) {
        return this.totalPages;
      }
      return this.defaultPagesToDisplay;
    },
    minPage() {
      if (this.page >= this.pagesToDisplay) {
        const pagesToAdd = Math.floor(this.pagesToDisplay / 2);
        const newMaxPage = pagesToAdd + this.page;
        if (newMaxPage > this.totalPages) {
          return this.totalPages - this.pagesToDisplay + 1;
        }
        return this.page - pagesToAdd;
      } else {
        return 1;
      }
    },
    maxPage() {
      if (this.page >= this.pagesToDisplay) {
        const pagesToAdd = Math.floor(this.pagesToDisplay / 2);
        const newMaxPage = pagesToAdd + this.page;
        if (newMaxPage < this.totalPages) {
          return newMaxPage;
        } else {
          return this.totalPages;
        }
      } else {
        return this.pagesToDisplay;
      }
    },
  },
  data() {
    return {
      defaultPagesToDisplay: 5,
    };
  },
  methods: {
    range(min, max) {
      let arr = [];
      for (let i = min; i <= max; i++) {
        arr.push(i);
      }
      return arr;
    },
    changePage(item) {
      if (item !== this.page) {
        this.$emit("update:modelValue", item);
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.$emit("update:modelValue", this.page + 1);
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.$emit("update:modelValue", this.page - 1);
      }
    },
    // Spec 003: First page navigation
    firstPage() {
      if (this.page !== 1) {
        this.$emit("update:modelValue", 1);
      }
    },
    // Spec 003: Last page navigation
    lastPage() {
      if (this.page !== this.totalPages) {
        this.$emit("update:modelValue", this.totalPages);
      }
    },
  },
  watch: {
    perPage() {
      this.$emit("update:modelValue", 1);
    },
    total() {
      this.$emit("update:modelValue", 1);
    },
  },
};
</script>

<style scoped>
.pagination .page-link {
  cursor: pointer;
}
.pagination .page-item.disabled .page-link {
  cursor: not-allowed;
  opacity: 0.5;
  pointer-events: none;
}
</style>
